import React, { useEffect, useState } from 'react';
import { Box, Button, Snackbar } from '@mui/material';
import { AddLink as AddLinkIcon } from '@mui/icons-material';
import { api } from '../../services/api';
import { logSharableLinkCreateOpened } from '../../services/analytics';
import DistinctiveDialog, { DistinctiveValues } from '../../dialogs/others/DistinctiveDialog';
import { DistinctiveTemplate, SHARE_LINK_UNLOCK_TEMPLATES } from '../../distinctive/distinctiveTemplates';
import { CreateShareLinkDialog } from './CreateShareLinkDialog';
import { isProjectShareable, loadEligibleProjects } from './eligibleProjectsCache';
import { NewBadge } from '../common';

/**
 * "Shareable Link" pill for the project header, in the My Shareable Links sunset
 * theme beside the gold Super Ratio pill. Opens the Create Share Link dialog on this project. Hidden until
 * the project is known to be eligible for 3D sharing (north calibrated).
 */
export function ProjectShareLinkButton({ projectId, projectName, playState = 'running' }: {
  projectId: string;
  projectName: string;
  /** Pauses the looping animations while the header is mid-collapse. */
  playState?: 'running' | 'paused';
}) {
  const [eligible, setEligible] = useState(false);
  const [open, setOpen] = useState(false);
  // Extra blocks unlocked? Same rule as My Shareable Links; a failed load leaves them open (the server still gates them).
  const [unlocked, setUnlocked] = useState<boolean | null>(null);
  const [unlockTemplate, setUnlockTemplate] = useState<DistinctiveTemplate | null>(null);
  const [distinctiveValues, setDistinctiveValues] = useState<DistinctiveValues>({ agentLine: '', agencyLine: 'Huttons Asia Pte Ltd · L3008899K' });
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setEligible(false);
    isProjectShareable(projectId)
      .then((ok) => {
        if (cancelled || !ok) return;
        setEligible(true);
        // Warm the dialog's project list so it opens without a spinner.
        loadEligibleProjects().catch(() => {});
      })
      .catch(() => { /* No button rather than a broken one. */ });
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (!open || unlocked !== null) return;
    let cancelled = false;
    api.getShareLinks()
      .then((res) => { if (!cancelled) setUnlocked(res.unlocked); })
      .catch(() => { if (!cancelled) setUnlocked(true); });
    api.getCurrentUser()
      .then((user) => {
        if (cancelled) return;
        setDistinctiveValues((prev) => ({
          ...prev,
          agentLine: [user.Name, user.CEANumber, user.Mobile || user.Email]
            .map((part) => (part || '').trim())
            .filter(Boolean)
            .join(' · '),
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, unlocked]);

  if (!eligible) return null;

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          flexShrink: 0,
          // Sunset glow, pulsed via opacity so the shadow never re-rasterises.
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 999,
            boxShadow: '0 0 0 1px rgba(255, 226, 122, 0.45), 0 10px 28px rgba(255, 61, 110, 0.6), 0 6px 22px rgba(139, 61, 255, 0.5)',
            opacity: 0,
            animation: 'shareLinkGlow 3.2s ease-in-out infinite',
            animationPlayState: playState,
            pointerEvents: 'none',
            '@keyframes shareLinkGlow': {
              '0%, 100%': { opacity: 0 },
              '50%': { opacity: 1 },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          },
        }}
      >
        <Button
          onClick={(e) => {
            e.stopPropagation();
            logSharableLinkCreateOpened({ source: 'project_header' });
            setOpen(true);
          }}
          onMouseDown={stop}
          onFocus={stop}
          startIcon={
            // Yellow chip from the My Shareable Links title highlight, link wiggling like its 🔗.
            <Box
              component="span"
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: { xs: 20, sm: 22 },
                height: { xs: 20, sm: 22 },
                borderRadius: '50%',
                bgcolor: '#FFE27A',
                color: '#1E1B3A',
                boxShadow: '0 2px 6px rgba(30, 27, 58, 0.35)',
              }}
            >
              <AddLinkIcon
                sx={{
                  fontSize: '0.95rem !important',
                  animation: 'shareLinkWiggle 2.8s ease-in-out infinite',
                  animationPlayState: playState,
                  '@keyframes shareLinkWiggle': {
                    '0%, 60%, 100%': { transform: 'rotate(0deg)' },
                    '70%': { transform: 'rotate(-18deg)' },
                    '80%': { transform: 'rotate(14deg)' },
                    '90%': { transform: 'rotate(-6deg)' },
                  },
                }}
              />
            </Box>
          }
          sx={{
            position: 'relative',
            overflow: 'hidden',
            pl: { xs: 0.6, sm: 0.75 },
            pr: { xs: 1.5, sm: 2.1 },
            py: { xs: 0.5, sm: 0.55 },
            minWidth: 0,
            borderRadius: 999,
            color: '#fff',
            fontWeight: 800,
            fontSize: { xs: '0.68rem', sm: '0.74rem' },
            lineHeight: 1,
            letterSpacing: { xs: '0.06em', sm: '0.13em' },
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            textShadow: '0 1px 6px rgba(30, 27, 58, 0.45)',
            border: '1px solid rgba(255, 255, 255, 0.35)',
            background: 'linear-gradient(110deg, #FF8A00 0%, #FF3D6E 35%, #8B3DFF 60%, #FF3D6E 80%, #FF8A00 100%)',
            backgroundSize: '260% 100%',
            backgroundPosition: '0% 50%',
            animation: 'shareLinkFlow 8s ease-in-out infinite',
            animationPlayState: playState,
            boxShadow: '0 6px 20px rgba(255, 61, 110, 0.4), 0 4px 14px rgba(139, 61, 255, 0.3)',
            transition: 'transform 160ms ease, box-shadow 220ms ease',
            '& .MuiButton-startIcon': { ml: 0, mr: { xs: 0.75, sm: 1 } },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: '40%',
              background: 'linear-gradient(100deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)',
              transform: 'translate3d(-140%, 0, 0) skewX(-22deg)',
              willChange: 'transform',
              animation: 'shareLinkSheen 4.2s ease-in-out infinite',
              animationPlayState: playState,
              pointerEvents: 'none',
            },
            '&:hover': {
              transform: 'translateY(-1px) scale(1.02)',
              boxShadow: '0 10px 28px rgba(255, 61, 110, 0.6), 0 6px 20px rgba(139, 61, 255, 0.5)',
            },
            '&:active': { transform: 'translateY(0) scale(0.99)' },
            '&:focus-visible': { outline: '3px solid #FFE27A', outlineOffset: 3 },
            '@keyframes shareLinkFlow': {
              '0%': { backgroundPosition: '0% 50%' },
              '50%': { backgroundPosition: '100% 50%' },
              '100%': { backgroundPosition: '0% 50%' },
            },
            '@keyframes shareLinkSheen': {
              '0%': { transform: 'translate3d(-140%, 0, 0) skewX(-22deg)' },
              '55%, 100%': { transform: 'translate3d(320%, 0, 0) skewX(-22deg)' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              '&::after': { animation: 'none', opacity: 0 },
              '& .MuiButton-startIcon svg': { animation: 'none' },
            },
          }}
        >
          Shareable Link
        </Button>
        <NewBadge
          sx={{
            position: 'absolute',
            top: -9,
            left: -11,
            zIndex: 2,
            animationPlayState: playState,
          }}
        />
      </Box>

      {/* Portalled dialogs: keep their clicks away from the header accordion. */}
      <Box onClick={stop} onMouseDown={stop} onFocus={stop} sx={{ display: 'contents' }}>
        <CreateShareLinkDialog
          open={open}
          onClose={() => setOpen(false)}
          initialProject={{ id: projectId, name: projectName }}
          featuresUnlocked={unlocked !== false}
          onLockedFeature={() => setUnlockTemplate(
            SHARE_LINK_UNLOCK_TEMPLATES[Math.floor(Math.random() * SHARE_LINK_UNLOCK_TEMPLATES.length)],
          )}
          onCreated={() => setToast('Link created — find it in My Shareable Links')}
        />
        {unlockTemplate && (
          <DistinctiveDialog
            open
            onClose={() => setUnlockTemplate(null)}
            values={distinctiveValues}
            templates={[unlockTemplate]}
            title="Unlock this block"
            description="Tap the design, then share it out once to unlock Price Promo, Thumbs-up, Valuation and Project Request."
            hideInterestActions
            onShared={() => {
              setUnlocked(true);
              setToast('Blocks unlocked — close this to pick them');
            }}
          />
        )}
        <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} message={toast}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
      </Box>
    </>
  );
}
